import type { ReactElement } from 'react';

import { ExportMenu } from '@/components/ui/ExportMenu';
import { CardFrame, CardNote, QueryBody, RANGE_NOTE } from '@/components/ui/card-frame';
import { stageKindLabel } from '@/lib/stage-kind';

import { analysisQuery, formatHours, rangeProblem } from '../presentation';
import type { DeviationRow, TimeStudyFilters } from '../types';
import { useDeviation } from '../use-time-study';

// Tiempo medio por tipo de documento y etapa (reunión 2026-10-27): cuánto toma en promedio
// cada etapa de, por ejemplo, un registro; es la línea base para fijar los estándares.
// La capacitación se mide por calendario, como en el resto del estudio (G4).

const ORDER = ['elaboracion', 'revision', 'aprobacion', 'capacitacion'] as const;
const GROUP_BY = 'documentType,stage';

interface Props {
  filters: TimeStudyFilters;
  companyId: string;
}

interface TypeGroup {
  id: string;
  name: string;
  rows: DeviationRow[];
}

function byType(rows: DeviationRow[]): TypeGroup[] {
  const groups = new Map<string, TypeGroup>();
  for (const r of rows) {
    const t = r.keys.documentType;
    if (t === undefined) {
      continue;
    }
    const g = groups.get(t.id) ?? { id: t.id, name: t.name ?? 'Tipo sin nombre', rows: [] };
    g.rows.push(r);
    groups.set(t.id, g);
  }
  const rank = (r: DeviationRow): number => ORDER.indexOf(r.keys.stage ?? 'elaboracion');
  return [...groups.values()]
    .map((g) => ({ ...g, rows: g.rows.sort((a, b) => rank(a) - rank(b)) }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

/** El promedio cuenta solo las etapas con tiempo registrado; si son menos que las
 *  terminadas, se dice sobre cuántas se calculó. */
function average(r: DeviationRow): string {
  const etapas = (n: number): string => `${n} ${n === 1 ? 'etapa' : 'etapas'}`;
  const calendar = r.basis === 'calendar' ? ' (calendario)' : '';
  if (r.avgEffortSeconds === null || r.measuredStageCount === 0) {
    return `Sin tiempo registrado · ${etapas(r.stageCount)}${calendar}`;
  }
  const n =
    r.measuredStageCount === r.stageCount
      ? etapas(r.stageCount)
      : `${r.measuredStageCount} de ${r.stageCount} etapas con tiempo`;
  return `${formatHours(r.avgEffortSeconds)} · ${n}${calendar}`;
}

export function AverageByTypeCard({ filters, companyId }: Props): ReactElement {
  const problem = rangeProblem(filters);
  const query = useDeviation(GROUP_BY, filters, { companyId, enabled: problem === null });
  return (
    <CardFrame
      title="Tiempo medio por tipo y etapa"
      count="promedio de las etapas con tiempo registrado"
      actions={
        problem === null && (
          <ExportMenu
            compact
            path="/api/v1/quality/productivity/deviation/export"
            query={`groupBy=${GROUP_BY}&${analysisQuery(filters)}`}
            title="Tiempo medio por tipo y etapa"
          />
        )
      }
    >
      {problem !== null ? (
        <CardNote>{RANGE_NOTE}</CardNote>
      ) : (
        <QueryBody
          query={query}
          isEmpty={(d) => d.rows.length === 0}
          emptyText="No hay trabajos terminados en el rango."
        >
          {(d) => (
            <div className="cb avgtypes">
              {byType(d.rows).map((g) => (
                <div key={g.id} role="group" aria-label={g.name}>
                  <div className="eyebrow mxhead">{g.name}</div>
                  <dl className="kvs">
                    {g.rows.map((r) => (
                      <div key={r.keys.stage} className="kv">
                        <dt className="kk">{stageKindLabel(r.keys.stage ?? 'elaboracion')}</dt>
                        <dd className="vv">{average(r)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
              <p className="soft fld-hint">
                Sirve como línea base para fijar los estándares de cada tipo.
              </p>
            </div>
          )}
        </QueryBody>
      )}
    </CardFrame>
  );
}
